// The length of a window_kind=recurring_event window, in the three shapes the
// admin reads it in outside the form's own boxes: as a badge, as the server's
// refusal of one, and as the sentence that tells an operator what the pause
// between two windows will actually look like on this board.
//
// Its own module rather than a handful of helpers inside LeaderboardConfigForm
// because most of its callers are somewhere else — the list and the detail
// page draw the badge, the two hosts of the form catch the refusal — and
// because `react-refresh/only-export-components` refuses a non-component
// export from a module that renders one. Everything here is pure, which is the
// other half of why it is worth separating: it is tested directly rather than
// through a render.

import { ApiError, errorReason } from "../../lib/errors";

/** The units a window length is counted in, smallest first — the order the
   form's picker lists them in, which is the order a duration is read in. */
export type WindowUnit = "minutes" | "hours" | "days";

/** What one of each is worth in the minutes that go on the wire. */
export const WINDOW_UNIT_MINUTES: Record<WindowUnit, number> = {
  minutes: 1,
  hours: 60,
  days: 1440,
};

const UNIT_SUFFIX: Record<WindowUnit, string> = {
  minutes: "m",
  hours: "h",
  days: "d",
};

/**
 * `window_minutes` as a number and the unit that says it whole: 4320 → 3 days,
 * 720 → 12 hours, 90 → 90 minutes.
 *
 * The largest unit the number divides into exactly, and never a compound —
 * "25h" rather than "1d 1h", because the badge this feeds shares a table cell
 * with the schedule and a second space in it reads as a second value.
 * Exactness is what keeps that honest: nothing is rounded away, so "3d" is
 * 4320 minutes and nothing else.
 */
export function splitWindowLength(minutes: number): {
  value: number;
  unit: WindowUnit;
} {
  // The `>= 1440` guard is about 0 alone: it divides by everything, and "0d"
  // would be the one reading of it nobody means.
  if (minutes >= 1440 && minutes % 1440 === 0) {
    return { value: minutes / 1440, unit: "days" };
  }
  if (minutes % 60 === 0) return { value: minutes / 60, unit: "hours" };
  return { value: minutes, unit: "minutes" };
}

/**
 * `window_minutes` as the badges print it: "3d", "12h", "90m".
 *
 * Written off splitWindowLength rather than beside it, because the form's
 * length box is filled from the same split: two spellings of the rule would
 * let the badge in the list and the form say different things about one board.
 */
export function formatWindowLength(minutes: number): string {
  const { value, unit } = splitWindowLength(minutes);
  return `${value}${UNIT_SUFFIX[unit]}`;
}

/**
 * Anything the server can say about `window_minutes`. The overlap refusal —
 * the one an operator actually meets — never names the field, so the sentence
 * it is written as has to be recognised too.
 *
 * Matched on the text because that is all there is: the error envelope carries
 * a code shared by every validation failure and no field name at all. What
 * this decides is only WHERE the message is printed, never what it says.
 */
const WINDOW_LENGTH_REASON =
  /window_minutes|is still open when the next one starts/;

/**
 * The server's own words for why it refused a window length, ready to print
 * under the field — or `undefined` when the refusal was about something else,
 * which leaves it to the host's banner.
 *
 * The numbers in it (the length it read, the gap between two starts) come from
 * the cron walk only the server does: it parses the expression with the same
 * library the cycle worker uses, and a second walk in the browser would be a
 * guess shown to the operator as a fact.
 */
export function windowLengthRefusal(err: unknown): string | undefined {
  // A REFUSAL, which is the 400 the validator answers with — not a 409 that
  // lost a race, not a 500 from somewhere else in the write. The text match
  // below is a broad instrument (the envelope carries no field name at all,
  // only a code every validation failure shares), and without the status
  // beside it any error whose wording happened to contain «window_minutes»
  // would be printed under the field and paint the length red for a fault
  // that is not its own.
  if (!(err instanceof ApiError) || err.status !== 400) return undefined;
  const reason = errorReason(err);
  if (!reason) return undefined;
  return WINDOW_LENGTH_REASON.test(reason) ? asSentence(reason) : undefined;
}

/**
 * A Go error string as a line of UI copy.
 *
 * Server reasons arrive as lowercase fragments with no full stop — ST1005, the
 * convention every error in the leaderboard package follows, because a Go
 * error is meant to be wrapped into a longer one. Nothing wraps this one: it
 * is printed on its own under a field, where a sentence is what the reader
 * expects and the rest of the form's messages already are.
 */
function asSentence(fragment: string): string {
  const trimmed = fragment.trim();
  if (!trimmed) return trimmed;
  // Everything but a first word that is a field name. `window_minutes` is the
  // key of the API body, and `Window_minutes` is not that key — the operator
  // reads it to find the field, so it stays spelled the way it is spelled.
  const opener = trimmed.split(" ", 1)[0];
  const capitalised = opener.includes("_")
    ? trimmed
    : trimmed[0].toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
}

/**
 * The first line of the "Windows do not run back to back" banner, told about
 * THIS board rather than about the idea of one — the mockup writes it with the
 * schedule's own weekday and the two times, because that is what makes the
 * pause concrete for the operator reading it.
 *
 * Derivable only for the family the sentence is shaped for: one weekday, one
 * time of day. It is arithmetic on a weekday and a clock, not a walk over the
 * schedule's ticks — nothing here parses intervals or predicts a date, which
 * stays the server's job (see windowLengthRefusal). Every other expression —
 * hourly, monthly, a list of days — gets the general sentence instead, the way
 * the rest of this form names a date when it has one and describes it when it
 * does not.
 */
export function windowPauseSentence(
  cron: string,
  minutes: number | null,
): string {
  const start = weeklyStart(cron);
  if (!start || minutes === null || minutes <= 0 || minutes >= WEEK_MINUTES) {
    return "This board opens on the schedule above, closes once the window's time is up and then stays shut until the next start.";
  }
  const end = (start.at + minutes) % WEEK_MINUTES;
  return (
    `This board opens ${WEEKDAYS[Math.floor(start.at / DAY_MINUTES)]} at ${clock(start.at)} UTC, ` +
    `closes ${WEEKDAYS[Math.floor(end / DAY_MINUTES)]} at ${clock(end)} UTC and then ` +
    `stays shut until the next ${WEEKDAYS[Math.floor(start.at / DAY_MINUTES)]}.`
  );
}

const DAY_MINUTES = 1440;
const WEEK_MINUTES = 7 * DAY_MINUTES;

/** Sunday first, the order cron itself numbers the days in. */
const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const DOW: Record<string, number> = {
  "0": 0,
  "7": 0,
  "1": 1,
  "2": 2,
  "3": 3,
  "4": 4,
  "5": 5,
  "6": 6,
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

/**
 * A 5-field cron as «this many minutes into the week», or null when it does
 * not fire exactly once a week at one time — a list, a range, a step, a day of
 * the month or a wildcard hour all mean the sentence above cannot be told.
 */
function weeklyStart(cron: string): { at: number } | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [minute, hour, dom, mon, dow] = parts;
  if (dom !== "*" || mon !== "*") return null;
  if (!/^\d{1,2}$/.test(minute) || !/^\d{1,2}$/.test(hour)) return null;
  const m = Number(minute);
  const h = Number(hour);
  if (m > 59 || h > 23) return null;
  const day = DOW[dow.toUpperCase()];
  if (day === undefined) return null;
  return { at: day * DAY_MINUTES + h * 60 + m };
}

/** Minutes into the week as the clock time of that day: "00:00", "17:30". */
function clock(at: number): string {
  const inDay = at % DAY_MINUTES;
  const h = String(Math.floor(inDay / 60)).padStart(2, "0");
  const m = String(inDay % 60).padStart(2, "0");
  return `${h}:${m}`;
}
